// @ts-nocheck
import {create} from "zustand";
import {listGamesService} from "../services/games/gamesList/listGames.service";
import {listMyFavoriteGamesService} from "../services/games/miniGames/favoriteGames.service";
import {initMiniGameService} from "../services/games/miniGames/initMiniGame.service";
import {getQueryString} from "../util/query";
import {createOption} from "../util/text";
import {useAuthStore} from "./useAuth.store";
import useCurrencyStore from "./useCurrency.store";

//sort list by isFav true first
const sortGamesIsFav = list => {
  return list.sort((a, b) => {
    if (a.isFav && !b.isFav) {
      return -1;
    }
    if (!a.isFav && b.isFav) {
      return 1;
    }
    return 0;
  });
};

export const useMiniGames = create((set, get) => ({
  list: [],
  gameUrl: null,
  loading: false,
  error: null,
  screenMode: window.innerWidth <= 768 ? "fullscreen" : "centered", // Mobile: fullscreen, Desktop: centered
  load: async () => {
    set({error: null});
  },
  unload: async () => {
    set({gameUrl: null});
  },
  setScreenMode: mode => {
    set({screenMode: mode});
  },
  resetScreenModeToDefault: () => {
    const defaultMode = window.innerWidth <= 768 ? "fullscreen" : "centered";
    set({screenMode: defaultMode});
  },
  search: "",
  filteredList: [],
  favoriteGames: [],
  favShow: false,
  provider: "",
  class: "",
  filtersVisible: false,
  providerOptions: [],
  classOptions: [],
  setFiltersVisible: () => {
    const filtersVisible = !get().filtersVisible;
    set({filtersVisible});
  },
  resetFilters: () => {
    set({
      provider: "",
      class: "",
      favShow: false,
      search: "", // Reset search as well if needed
    });
  },
  setSearch: search => {
    set({search});

    // Filter the list only if there's a search term
    if (search) {
      const filteredList = get().list.filter(game => {
        return game.name.toLowerCase().includes(search.toLowerCase());
      });
      set({filteredList});
    } else {
      // If no search term, reset the filtered list to the original list
      set({filteredList: get().list});
    }
  },
  setFavoriteGames: (game, addFavoriteGame, deleteFavoriteGame) => {
    const {favoriteGames} = get();
    const provider = game.provider;
    const existingGameIndex = favoriteGames.findIndex(
      item => item.game_id === game.id && item.game_provider === provider
    );

    if (existingGameIndex !== -1) {
      // Game already exists, remove it
      const updatedFavoriteGames = [...favoriteGames];
      updatedFavoriteGames.splice(existingGameIndex, 1);
      set({favoriteGames: updatedFavoriteGames});

      // Update the game in the list to mark it as not favorite
      const updatedList = get().list.map(item => {
        if (item.id === game.id) {
          return {...item, isFav: false};
        }
        return item;
      });
      deleteFavoriteGame(game.id);
      set({
        filteredList: sortGamesIsFav(updatedList),
        list: sortGamesIsFav(updatedList),
        favShow: false,
      });
    } else {
      // Game doesn't exist, add it
      set({
        favoriteGames: [...favoriteGames, {game_id: game.id, game_provider: provider}],
      });

      const updatedList = get().list.map(item => {
        if (item.id === game.id) {
          return {...item, isFav: true};
        }
        return item;
      });
      addFavoriteGame({game_id: game.id, game_provider: provider});
      set({filteredList: sortGamesIsFav(updatedList), list: updatedList});
    }
  },
  setFavShow: bool => {
    // Reset the provider and class to show all favs - user will be able to filter once in fav view
    set({provider: "", class: ""});
    if (bool) {
      // Filter the list to include only favorite games
      const filteredList = get().list.filter(game => game.isFav);
      set({filteredList});
    } else {
      // Apply provider and class filters when favShow is false
      const filteredList = get().list.filter(game => {
        const matchesProvider = !get().provider || game.provider === get().provider;
        const matchesClass = !get().class || game.class === get().class;
        return matchesProvider && matchesClass;
      });
      set({filteredList});
    }
    set({favShow: bool});
  },
  setProvider: option => {
    set({provider: option.value});
  },
  setClass: option => {
    set({class: option.value});
  },
  getConfig: async (provider, id) => {
    set({loading: true, error: false});

    try {
      // Get the selected currency from the currency store
      const selectedCurrency = useCurrencyStore.getState().selectedCurrency;
      const currencyParam = selectedCurrency ? selectedCurrency.id : "VES";
      const isMobile = window.innerWidth <= 768;

      const response = await initMiniGameService({provider, id, currency: currencyParam, isMobile});
      const {gameUrl = null} = response?.data?.data || "";
      set({gameUrl, loading: false});
    } catch (e) {
      set({loading: false, error: {message: e.message, code: e.status}});
    }
  },
  getMiniGames: async (provider, classFilter) => {
    try {
      const authStatus = useAuthStore.getState().authStatus;
      let favGames = [];

      // Allow empty/null provider when classFilter is provided (e.g., for "live games")
      if (!provider && !classFilter) return;

      if (provider === "all") {
        provider = "";
      }
      // Set loading to true before the API call
      set({loading: true});

      const queryString = getQueryString({
        provider: provider,
        class: classFilter,
      });

      const response = await listGamesService(queryString);

      const providerOptions = response.data.data.game_providers.map(item => createOption(item));
      const classOptions = response.data.data.game_classes.map(item => createOption(item));

      // Construct the list from the API response
      const list = response.data.data.providers.reduce((acc, item) => {
        item.games.forEach(game => {
          acc.push(game);
        });
        return acc;
      }, []);

      if (authStatus === "authenticated") {
        const favGamesResp = await listMyFavoriteGamesService();
        favGames = favGamesResp?.data?.data || []; // Handle empty response

        // Add 'isFav' to games only after the list is populated
        list.forEach(game => {
          const isFav = favGames.some(item => item.game_id === game.id && item.game_provider === game.provider);

          game.isFav = isFav;
        });
      }

      set({
        providerOptions,
        classOptions,
        list,
        filteredList: sortGamesIsFav(list), // Sort after adding 'isFav'
        favoriteGames: favGames,
        loading: false, // Set loading to false after the API call and processing
      });
    } catch (e) {
      set({
        loading: false,
        error: {message: e?.message || "Error cargando juegos", code: e?.status},
      });
    }
  },
}));
